import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShoppingBag, BookOpen } from "lucide-react";
import { books } from "@/data/books";
import BookCover from "./BookCover";
import { useCart } from "@/hooks/useCart";
import { toast } from "sonner";

export default function BooksShelf() {
  const { addItem } = useCart();

  return (
    <section
      id="ksiazki"
      className="relative py-28 md:py-40 overflow-hidden"
      data-testid="books-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="mb-16 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <div>
            <span className="chip" data-testid="books-eyebrow">
              <span className="dot" /> Nasze książki
            </span>
            <h2 className="font-display font-semibold text-[#051937] text-4xl sm:text-5xl lg:text-6xl mt-6 tracking-tight leading-[1.02] max-w-3xl">
              Osiem tomów, jedna banda —{" "}
              <em className="not-italic text-[#60A5FA]">tysiąc razy pod kołdrą.</em>
            </h2>
          </div>
          <p className="text-[#0b2a55]/75 text-lg max-w-sm">
            Twarda oprawa, papier z recyklingu, miejsce na imię dziecka.
            Każdy tom to inna przygoda — możesz je kupić pojedynczo lub w kolekcji.
          </p>
        </motion.div>

        <motion.ul
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8"
        >
          {books.map((b) => (
            <motion.li
              key={b.slug}
              variants={{
                hidden: { opacity: 0, y: 40 },
                show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
              }}
              whileHover={{ y: -8 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
              className="group"
              data-testid={`book-card-${b.slug}`}
            >
              <Link to={`/ksiazka/${b.slug}`} className="block">
                <BookCover book={b} />
              </Link>
              <div className="mt-4 flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                  <Link
                    to={`/ksiazka/${b.slug}`}
                    className="font-display font-semibold text-[#051937] text-lg leading-tight group-hover:text-[#60A5FA] transition-colors"
                  >
                    {b.title}
                  </Link>
                  <span className="font-display font-semibold text-[#051937] whitespace-nowrap">
                    {b.price_pln} zł
                  </span>
                </div>
                <p className="text-xs uppercase tracking-[0.14em] text-[#0b2a55]/60 font-bold">
                  {b.subtitle}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      addItem(b.lookup_key);
                      toast.success(`${b.title} — dodano do koszyka 📚`);
                    }}
                    data-testid={`add-cart-${b.slug}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#051937] hover:bg-[#0b2a55] text-white px-4 py-2 text-xs font-semibold transition-colors"
                  >
                    <ShoppingBag size={14} /> Do koszyka
                  </button>
                  <Link
                    to={`/ksiazka/${b.slug}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#BAE6FD] hover:bg-[#F4FAFF] text-[#051937] px-4 py-2 text-xs font-semibold"
                    data-testid={`view-book-${b.slug}`}
                  >
                    <BookOpen size={14} /> Zajrzyj
                  </Link>
                </div>
              </div>
            </motion.li>
          ))}
        </motion.ul>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="mt-14 md:mt-20 text-center"
        >
          <Link
            to="/sklep"
            data-testid="books-to-shop"
            className="inline-flex items-center gap-2 rounded-full bg-[#051937] hover:bg-[#0b2a55] text-white px-7 py-4 font-semibold shadow-[0_20px_50px_-20px_rgba(11,42,85,0.6)]"
          >
            Wejdź do sklepu <ArrowUpRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
